import { useState } from "react";
import { Upload, CheckCircle2, ExternalLink, GripVertical, FileText, AlertTriangle, Trash2 } from "lucide-react";
import { Avatar } from "./Avatar";
import { fmtDate, fmtMoney } from "./utils";

// Kanban card — one migration_client row. Draggable, clickable to open
// the drawer. Renders every flag pill (Signed, Plan mismatch, Syncro,
// contract-urgency, missing-contact) inline so ops can scan a column
// without opening each card.

export function ClientCard({ client, onOpen, isPartnerView, syncroUrlBase }) {
  // Signed via wizard link OR via admin-uploaded manual form.
  const isSigned = !!(client.linked_signup_id || client.manual_signup);
  const signedByManual = !client.linked_signup_id && !!client.manual_signup;
  const monthly = Number(client.current_monthly_fee) || 0;
  const noteCount = Number(client.note_count) || 0;
  const syncroSynced = !isPartnerView
    && (client.syncro_status === "synced" || !!client.syncro_ticket_number || !!client.syncro_customer_id);
  const syncroTicketUrl = syncroSynced && syncroUrlBase && client.syncro_ticket_id
    ? `${syncroUrlBase}/tickets/${client.syncro_ticket_id}` : null;
  const [dragging, setDragging] = useState(false);

  function onDragStart(e) {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-migration-client", client.id);
    // Some browsers require any text/plain payload too
    e.dataTransfer.setData("text/plain", client.id);
    setDragging(true);
  }
  function onDragEnd() { setDragging(false); }

  // Contract urgency (green >90d out, amber 30-90d, red <30d, "READY" if past)
  let contractBadge = null;
  if (client.contract_end_date) {
    const end = new Date(client.contract_end_date);
    const days = Math.floor((end - new Date()) / (86400 * 1000));
    if (!isNaN(days)) {
      if (days <= 0) {
        contractBadge = { color: "emerald", label: "READY", title: `Contract ended ${fmtDate(client.contract_end_date)}` };
      } else if (days < 30) {
        contractBadge = { color: "red", label: `${days}d`, title: `Contract ends ${fmtDate(client.contract_end_date)} — urgent` };
      } else if (days < 90) {
        contractBadge = { color: "amber", label: `${days}d`, title: `Contract ends ${fmtDate(client.contract_end_date)}` };
      } else {
        contractBadge = { color: "slate", label: `${days}d`, title: `Contract ends ${fmtDate(client.contract_end_date)}` };
      }
    }
  }

  return (
    <div
      data-testid={`client-card-${client.id}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`rounded-lg border p-3 hover:border-slate-700 transition-all cursor-grab active:cursor-grabbing select-none ${
        isSigned ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-800 bg-slate-900"
      } ${dragging ? "opacity-40 scale-[0.98]" : ""}`}
      title="Drag to another column to change stage"
    >
      <button onClick={() => onOpen(client)} className="text-left w-full">
        <div className="flex items-start gap-2.5">
          <Avatar client={client} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium text-slate-100 truncate flex-1 leading-snug">
                {client.business_name || client.contact_name || client.email || "(no name)"}
              </div>
              {isSigned && (
                <span
                  data-testid={`client-signed-pill-${client.id}`}
                  className="shrink-0 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 rounded px-1.5 py-0.5 font-bold"
                  title={signedByManual
                    ? `Signed offline — manual form uploaded${client.manual_signup?.uploaded_by ? " by " + client.manual_signup.uploaded_by : ""}`
                    : "Customer completed the signup wizard"}
                >
                  <CheckCircle2 className="w-2.5 h-2.5" />
                  {signedByManual ? "Signed (manual)" : "Signed"}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 truncate mt-0.5">
              {client.contact_name && client.contact_name !== client.business_name
                ? client.contact_name
                : client.email || client.phone || "—"}
            </div>
          </div>
          {/* Drag handle affordance — icon only, drag events fire on parent */}
          <GripVertical className="w-3.5 h-3.5 text-slate-700 shrink-0 mt-1" aria-hidden />
        </div>

        {(client.current_plan_name || monthly > 0) && (
          <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[11px]">
            <span className="text-slate-500">Current:</span>
            {client.current_plan_name && (
              <span className="text-slate-200 font-medium">{client.current_plan_name}</span>
            )}
            {monthly > 0 && (
              <span className="text-amber-300 font-mono">{fmtMoney(monthly)}<span className="text-slate-500">/mo</span></span>
            )}
          </div>
        )}
        {client.service_start_date && (
          <div className="text-[10px] text-slate-500 mt-0.5" title="Billing anchor / service anniversary">
            Since {fmtDate(client.service_start_date)}
          </div>
        )}

        {/* Submitted-at + stale indicator */}
        {client.created_at && (() => {
          const created = new Date(client.created_at);
          const days = Math.floor((Date.now() - created) / 86400000);
          if (Number.isNaN(days)) return null;
          let tone = "text-slate-500";
          let label = `${days}d ago`;
          if (days <= 0) label = "today";
          else if (days === 1) label = "1d ago";
          if (days >= 30) tone = "text-red-300 font-semibold";
          else if (days >= 15) tone = "text-amber-300 font-semibold";
          else if (days <= 6) tone = "text-slate-400";
          return (
            <div
              data-testid={`client-submitted-${client.id}`}
              className={`text-[10px] mt-0.5 ${tone}`}
              title={`Card created ${fmtDate(client.created_at)}`}
            >
              Submitted {label}
            </div>
          );
        })()}

        {/* Icon strip — status flags at a glance */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px]">
          {client.plan_mismatch?.is_mismatch && !client.plan_mismatch?.acknowledged && (
            <span
              data-testid={`client-plan-mismatch-pill-${client.id}`}
              title={`PLAN MISMATCH — review before activation. ${(client.plan_mismatch?.reasons || []).join(" ")}`}
              className="inline-flex items-center gap-1 bg-red-500/20 border border-red-500/60 text-red-200 rounded px-1.5 py-0.5 uppercase tracking-widest font-bold animate-pulse"
            >
              <AlertTriangle className="w-2.5 h-2.5" /> Plan mismatch
            </span>
          )}
          {client.plan_mismatch?.is_mismatch && client.plan_mismatch?.acknowledged && (
            <span
              data-testid={`client-plan-mismatch-acked-${client.id}`}
              title={`Mismatch acknowledged by ${client.plan_mismatch?.ack?.by || 'admin'}${client.plan_mismatch?.ack?.note ? ' — ' + client.plan_mismatch.ack.note : ''}`}
              className="inline-flex items-center gap-1 bg-amber-500/10 border border-amber-500/40 text-amber-300 rounded px-1.5 py-0.5 uppercase tracking-widest font-semibold"
            >
              <CheckCircle2 className="w-2.5 h-2.5" /> Mismatch OK&apos;d
            </span>
          )}
          {client.source === "port_list_import" && (
            <span
              data-testid={`client-imported-pill-${client.id}`}
              title="Bulk-imported from the Valve/Macktronix port list"
              className="inline-flex items-center gap-1 bg-violet-500/10 border border-violet-500/40 text-violet-300 rounded px-1.5 py-0.5 uppercase tracking-widest font-semibold"
            >
              <Upload className="w-2.5 h-2.5" /> Imported from Valve
            </span>
          )}
          {syncroSynced && (
            syncroTicketUrl ? (
              <a
                href={syncroTicketUrl}
                target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`client-syncro-link-${client.id}`}
                title={`Open Syncro ticket #${client.syncro_ticket_number || client.syncro_ticket_id} in a new tab`}
                className="inline-flex items-center gap-1 bg-sky-500/10 border border-sky-500/40 text-sky-300 rounded px-1.5 py-0.5 font-semibold hover:bg-sky-500/20"
              >
                <ExternalLink className="w-2.5 h-2.5" />
                Syncro #{client.syncro_ticket_number || client.syncro_ticket_id}
              </a>
            ) : (
              <span
                data-testid={`client-syncro-pill-${client.id}`}
                title="Synced with Syncro"
                className="inline-flex items-center gap-1 bg-sky-500/10 border border-sky-500/40 text-sky-300 rounded px-1.5 py-0.5 font-semibold"
              >
                Syncro synced
              </span>
            )
          )}
          {(noteCount > 0 || client.has_unread_activity) && (
            <span
              data-testid={`client-notes-pill-${client.id}`}
              data-unread={client.has_unread_activity ? "true" : "false"}
              title={
                client.has_unread_activity
                  ? (noteCount > 0
                      ? `${noteCount} note${noteCount === 1 ? "" : "s"} — new activity since your last check`
                      : "New activity since your last check")
                  : `${noteCount} note${noteCount === 1 ? "" : "s"} on this client`
              }
              className={
                "relative inline-flex items-center gap-1 rounded px-1.5 py-0.5 border " +
                (client.has_unread_activity
                  ? "bg-amber-500/20 border-amber-500/60 text-amber-200 animate-pulse font-semibold ring-1 ring-amber-400/40 shadow-[0_0_8px_rgba(251,191,36,0.35)]"
                  : "bg-slate-800 border-slate-700 text-slate-300")
              }
            >
              <FileText className="w-2.5 h-2.5" />
              {noteCount > 0 ? noteCount : "NEW"}
              {client.has_unread_activity && (
                <span
                  className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-slate-900 animate-ping"
                  aria-hidden
                />
              )}
            </span>
          )}
          {contractBadge && (
            <span
              data-testid={`client-contract-pill-${client.id}`}
              title={contractBadge.title}
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 border font-semibold
                ${contractBadge.color === "emerald" ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                  : contractBadge.color === "red"     ? "bg-red-500/15 border-red-500/50 text-red-300"
                  : contractBadge.color === "amber"   ? "bg-amber-500/15 border-amber-500/50 text-amber-300"
                  : "bg-slate-800 border-slate-700 text-slate-400"}`}
            >
              {contractBadge.color === "emerald" ? "✓" : "⏰"} {contractBadge.label}
            </span>
          )}
          {!client.email && !client.phone && (
            <span
              data-testid={`client-missing-info-${client.id}`}
              title="No email or phone — can't chase this customer yet"
              className="inline-flex items-center gap-1 bg-red-500/10 border border-red-500/40 text-red-300 rounded px-1.5 py-0.5"
            >
              ⚠️ No contact
            </span>
          )}
        </div>

        {client.chosen_plan && (
          <div className="mt-2 inline-block text-[10px] uppercase tracking-widest text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded px-1.5 py-0.5">
            → {client.chosen_plan}
          </div>
        )}
      </button>
    </div>
  );
}

// Silence unused-import lint for icons imported for future flag pills
// (kept so any downstream flag addition doesn't need to re-import).
const _unused = { Trash2 };
export default _unused;
